import type { AgentLicense, HREmployee } from './hrData';

/** Show a stored YYYY-MM-DD date in U.S. format (MM/DD/YYYY). Anything else is shown as-is. */
export const usDate = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${m[2]}/${m[3]}/${m[1]}` : iso;
};

export type LicenseUrgency = 'expired' | 'critical' | 'warning' | 'ok';

export const daysUntil = (expirationDate: string, today: Date): number => {
  const target = new Date(`${expirationDate}T00:00:00`);
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((target.getTime() - base.getTime()) / 86400000);
};

export const getLicenseUrgency = (days: number, leadDays: number): LicenseUrgency => {
  if (days < 0) return 'expired';
  if (days <= 7) return 'critical';
  if (days <= leadDays) return 'warning';
  return 'ok';
};

export interface ExpiringLicenseEntry {
  employee: HREmployee;
  license: AgentLicense;
  days: number;
  urgency: LicenseUrgency;
}

/** Every license expiring within `leadDays` (already-expired ones included), soonest first. */
export const getExpiringLicenses = (
  employees: HREmployee[],
  leadDays: number,
  today: Date,
): ExpiringLicenseEntry[] => {
  const entries: ExpiringLicenseEntry[] = [];
  employees.forEach(employee => {
    employee.licenses.forEach(license => {
      const days = daysUntil(license.expirationDate, today);
      if (days <= leadDays) {
        entries.push({ employee, license, days, urgency: getLicenseUrgency(days, leadDays) });
      }
    });
  });
  return entries.sort((a, b) => a.days - b.days);
};
